/**
 * Utility functions for handling deep link query parameters
 */

export interface DeepLinkParams {
  access_token?: string;
  refresh_token?: string;
  callback_url?: string;
}

const CALLBACK_URL_KEY = 'callback_url';
const DEEP_LINK_PROCESSING_KEY = 'deep_link_processing';
const MOBILE_CALLBACK_FLOW_KEY = 'mobile_callback_flow';

/**
 * Extract and store deep link parameters in localStorage
 * This function should be called when the user hits a deep link like:
 * https://lottoreco.dev.sofmen.com/add_store?refresh_token=${refreshToken}&access_token=${token}&callback_url=lottoreco://add_store
 */
export const handleDeepLinkParams = (): DeepLinkParams => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const accessToken = urlParams.get('access_token');
  const refreshToken = urlParams.get('refresh_token');
  const callbackUrl = urlParams.get('callback_url');
  
  const params: DeepLinkParams = {};
  
  // Store access_token temporarily for store creation (not as main auth token)
  if (accessToken) {
    localStorage.setItem('token', accessToken);
    params.access_token = accessToken;
    console.log('Deep link: Stored access_token temporarily for store creation');
  }
  
  // Store refresh_token temporarily for store creation (not as main auth token)
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
    params.refresh_token = refreshToken;
    console.log('Deep link: Stored refresh_token temporarily for store creation');
  }
  
  // Store callback_url as "callback_url" in localStorage
  if (callbackUrl) {
    localStorage.setItem(CALLBACK_URL_KEY, callbackUrl);
    // Set a flag to prevent immediate callback redirect
    localStorage.setItem(DEEP_LINK_PROCESSING_KEY, 'true');
    localStorage.setItem(MOBILE_CALLBACK_FLOW_KEY, 'true');
    params.callback_url = callbackUrl;
    console.log('Deep link: Stored callback_url in localStorage with processing flag');
  }
  
  // Clean up URL parameters after storing them
  if (accessToken || refreshToken || callbackUrl) {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('access_token');
    newUrl.searchParams.delete('refresh_token');
    newUrl.searchParams.delete('callback_url');
    
    // Update the URL without the deep link parameters
    window.history.replaceState({}, '', newUrl.toString());
    
    console.log('Deep link: Cleaned up URL parameters');
    
    // Redirect to /add_store route after processing deep link parameters
    if (window.location.pathname !== '/app/add_store') {
      console.log('Deep link: Redirecting to /add_store route');
      window.location.href = '/app/add_store';
    }
  }
  
  return params;
};

/**
 * Get callback URL from localStorage
 */
export const getCallbackUrl = (): string | null => {
  return localStorage.getItem(CALLBACK_URL_KEY);
};

/**
 * Clear callback URL from localStorage
 */
export const clearCallbackUrl = (): void => {
  localStorage.removeItem(CALLBACK_URL_KEY);
  console.log('Deep link: Cleared callback_url from localStorage');
};

/**
 * Get deep link access token (for store creation)
 */
export const getDeepLinkAccessToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Get deep link refresh token (for store creation)
 */
export const getDeepLinkRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

/**
 * Clear all deep link parameters from localStorage
 */
export const clearDeepLinkParams = (): void => {
  localStorage.removeItem(DEEP_LINK_PROCESSING_KEY);
  console.log('Deep link: Cleared temporary auth tokens and processing flag from localStorage');
};

/**
 * Clear callback URL from localStorage (used after successful redirect)
 */
export const clearCallbackUrlOnly = (): void => {
  localStorage.removeItem(CALLBACK_URL_KEY);
  console.log('Deep link: Cleared callback_url from localStorage');
};

/**
 * Check if the current callback redirect belongs to a mobile deep link flow
 */
export const isMobileCallbackFlow = (): boolean => {
  return localStorage.getItem(MOBILE_CALLBACK_FLOW_KEY) === 'true';
};

/**
 * Clear the mobile deep link callback flow flag
 */
export const clearMobileCallbackFlow = (): void => {
  localStorage.removeItem(MOBILE_CALLBACK_FLOW_KEY);
};

/**
 * Clear all authentication and deep link keys after the browser hands off to mobile
 */
export const clearMobileCallbackSession = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem(CALLBACK_URL_KEY);
  localStorage.removeItem(DEEP_LINK_PROCESSING_KEY);
  localStorage.removeItem(MOBILE_CALLBACK_FLOW_KEY);
  console.log('Deep link: Cleared mobile callback session data from localStorage');
};

/**
 * Check if we're in deep link processing mode
 */
export const isDeepLinkProcessing = (): boolean => {
  return localStorage.getItem(DEEP_LINK_PROCESSING_KEY) === 'true';
};

/**
 * Check if we have deep link parameters in the current URL
 */
export const hasDeepLinkParams = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return !!(urlParams.get('access_token') || urlParams.get('refresh_token') || urlParams.get('callback_url'));
};

/**
 * Check if we have stored deep link parameters in localStorage
 */
export const hasStoredDeepLinkParams = (): boolean => {
  return !!(localStorage.getItem(CALLBACK_URL_KEY));
};

/**
 * Get all stored deep link parameters
 */
export const getStoredDeepLinkParams = (): DeepLinkParams => {
  return {
    access_token: localStorage.getItem('token') || undefined,
    refresh_token: localStorage.getItem('refresh_token') || undefined,
    callback_url: localStorage.getItem(CALLBACK_URL_KEY) || undefined,
  };
};
